import React, { useState } from 'react';
import {
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Checkbox,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import styles from './BaseCard.module.scss';

export interface BaseCardProps {
  // Content
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  badges?: React.ReactNode[];

  // Selection
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;

  // Actions
  infoButton?: boolean;
  infoTooltip?: string;
  onInfo?: () => void;
  menuItems?: Array<{
    icon?: React.ReactNode;
    label: string;
    onClick: () => void;
    color?: 'default' | 'error';
  }>;

  // Styling
  variant?: 'standard' | 'mui';
  className?: string;
  selectedClassName?: string;
  inPackage?: boolean;
}

const BaseCard: React.FC<BaseCardProps> = ({
  icon,
  title,
  subtitle,
  badges,
  selectable = false,
  selected = false,
  onSelect,
  infoButton = true,
  infoTooltip = 'View Details',
  onInfo,
  menuItems = [],
  variant = 'standard',
  className,
  selectedClassName,
  inPackage = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const cardContent = (
    <div
      className={`
        ${variant === 'mui' ? styles.baseCardContent : styles.baseCard}
        ${selected ? styles.selected : ''}
        ${selectedClassName || ''}
        ${inPackage ? styles.inPackage : ''}
        ${className || ''}
      `}
    >
      {/* Selection Checkbox */}
      {selectable && onSelect && (
        <Checkbox
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          size="small"
          className={styles.checkbox}
        />
      )}

      {/* Icon */}
      <div className={`${styles.iconWrapper} ${typeof icon === 'string' ? styles.emoji : ''}`}>
        {icon}
      </div>

      {/* Info */}
      <div className={styles.info}>
        {title}
        {subtitle}
      </div>

      {/* Badges */}
      {badges && badges.length > 0 && (
        <div className={styles.badges}>
          {badges.map((badge, index) => (
            <React.Fragment key={index}>{badge}</React.Fragment>
          ))}
        </div>
      )}

      {/* Info Button */}
      {infoButton && onInfo && (
        <Tooltip title={infoTooltip}>
          <IconButton size="small" onClick={onInfo} className={styles.actionButton}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Menu */}
      {menuItems.length > 0 && (
        <>
          <IconButton size="small" onClick={handleMenuOpen} className={styles.actionButton}>
            <MoreVertIcon fontSize="small" />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {menuItems.map((item, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  item.onClick();
                  handleMenuClose();
                }}
                sx={item.color === 'error' ? { color: 'error.main' } : {}}
              >
                {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </div>
  );

  if (variant === 'mui') {
    return (
      <Card
        className={`${styles.baseCardMUI} ${selected ? styles.selected : ''} ${inPackage ? styles.inPackage : ''}`}
        variant="outlined"
      >
        <CardContent>{cardContent}</CardContent>
      </Card>
    );
  }

  return cardContent;
};

export default BaseCard;
